<script>
import { NOTIFICATION_FREQUENCY_OPTIONS } from '../../utils/constants'
export default {
    name: "settingEmailNotification",
    components: {},
    data: function () {
        return {
            user: this.$A.Auth,
            NOTIFICATION_FREQUENCY_OPTIONS
        };
    },
    computed: {
        userSettings: function () {
            return _.get(this, 'user.userSettings', {});
        },
        // Hour-granularity choices for the Daily digest send time ("HH:00" values).
        digestTimeOptions: function () {
            return Array.from({ length: 24 }, (_, hour) => {
                const label = new Date(2000, 0, 1, hour).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                return { value: `${String(hour).padStart(2, '0')}:00`, label };
            });
        },
        dailyDigestTimeOfDay: {
            get() {
                // Snap to the hour so API-set values like "14:30" still match an option.
                const value = this.userSettings.dailyDigestTimeOfDay || '17:00';
                return `${value.slice(0, 2)}:00`;
            },
            set(value) {
                this.$set(this.userSettings, 'dailyDigestTimeOfDay', value);
            },
        },
        notifyMeNewCommentsEnabled: {
            get() {
                return this.userSettings.notifyMeNewComments !== 0;
            },
            set(value) {
                this.userSettings.notifyMeNewComments = value ? 1 : 0;
            },
        },
        notifyMyApproversNewCommentsEnabled: {
            get() {
                return this.userSettings.notifyMyApproversNewComments !== 0;
            },
            set(value) {
                this.userSettings.notifyMyApproversNewComments = value ? 1 : 0;
            },
        },
    },
    methods: {
        formatFrequencyLabel: function (label) {
            return label.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
        },
        updateEmailNotificationSettings: async function () {
            let user = await this.$A.UserService.UpdateUserDefaultSettings(
                this.$A.Auth.id,
                this.userSettings.downloadFiles,
                this.userSettings.approveIndividualFiles,
                this.userSettings.viewAllComments,
                this.userSettings.approveWithChanges,
                this.userSettings.notifyFilesSent,
                this.userSettings.notifyFilesApproved,
                this.userSettings.notifyNewComments,
                this.userSettings.notifyMeNewComments,
                this.userSettings.notifyMyApproversNewComments,
                this.userSettings.limitVersionVisibility,
                this.userSettings.blockCommentingAfterProofDecision,
                this.userSettings.reminderDays,
                this.userSettings.reminderTimeOfDay,
                this.userSettings.reminderTimeZone,
                this.userSettings.watermarkEnabled,
                this.dailyDigestTimeOfDay,
                this.userSettings.notifyJobAssigned
            );
            if (user.id !== undefined && user.id > 0) {
                this.user = user;
                this.$A.UpdateUserObjectAndBustCache(user);
            }
        }
    },
};
</script>

<template>
    <div class="settings-card">
        <div class="card-head">
            <div class="card-head-title">Email Notifications</div>
            <div class="card-head-subtitle">Choose which emails Ashore sends you and your reviewers.</div>
        </div>
        <div class="toggle-rows">
            <div class="toggle-row">
                <div class="ashore-toggle toggle-control">
                    <label class="switch">
                    <input type="checkbox" v-model="userSettings.notifyFilesSent" />
                    <div class="slider round"></div>
                    </label>
                </div>
                <div class="toggle-text">
                    <div class="toggle-title">Notify me if a proof has been successfully sent</div>
                    <div class="toggle-description">Get a confirmation email each time one of your proofs goes out.</div>
                </div>
            </div>
            <div class="toggle-row">
                <div class="ashore-toggle toggle-control">
                    <label class="switch">
                    <input type="checkbox" v-model="userSettings.notifyFilesApproved" />
                    <div class="slider round"></div>
                    </label>
                </div>
                <div class="toggle-text">
                    <div class="toggle-title">Notify me if proof has been approved</div>
                    <div class="toggle-description">Hear about it the moment a proof you own gets approved.</div>
                </div>
            </div>
            <div class="toggle-row">
                <div class="ashore-toggle toggle-control">
                    <label class="switch">
                    <input type="checkbox" v-model="userSettings.notifyJobAssigned" />
                    <div class="slider round"></div>
                    </label>
                </div>
                <div class="toggle-text">
                    <div class="toggle-title">Notify me when I'm assigned to a job</div>
                    <div class="toggle-description">Get an email whenever someone adds you as an assignee on a job.</div>
                </div>
            </div>
            <div class="toggle-row">
                <div class="ashore-toggle toggle-control">
                    <label class="switch">
                    <input type="checkbox" v-model="notifyMyApproversNewCommentsEnabled" />
                    <div class="slider round"></div>
                    </label>
                </div>
                <div class="toggle-text">
                    <div class="toggle-title">Notify my reviewers about new comments on proofs I own</div>
                    <div class="toggle-description">Keep reviewers in the loop when new comments land.</div>
                    <div v-if="notifyMyApproversNewCommentsEnabled" class="nested-field">
                        <label class="field-label" for="notifyMyApproversNewCommentsFrequency">Notification Frequency</label>
                        <div class="select-wrap">
                            <select v-model="userSettings.notifyMyApproversNewComments" class="select-input"
                                id="notifyMyApproversNewCommentsFrequency">
                                <option v-for="(value, label) in NOTIFICATION_FREQUENCY_OPTIONS" :key="label" :value="value">
                                    {{ formatFrequencyLabel(label) }}
                                </option>
                            </select>
                            <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="1 1 5 5 9 1" />
                            </svg>
                        </div>
                    </div>
                    <div v-if="notifyMyApproversNewCommentsEnabled && userSettings.notifyMyApproversNewComments === NOTIFICATION_FREQUENCY_OPTIONS.DAILY" class="nested-field">
                        <label class="field-label" for="notifyMyApproversDailyTime">Daily Send Time</label>
                        <div class="select-wrap">
                            <select v-model="dailyDigestTimeOfDay" class="select-input" id="notifyMyApproversDailyTime">
                                <option v-for="option in digestTimeOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                            <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="1 1 5 5 9 1" />
                            </svg>
                        </div>
                        <div class="toggle-description">Daily digests send at this time ({{ userSettings.reminderTimeZone || 'America/Chicago' }}). One send time applies to all your daily digests.</div>
                    </div>
                </div>
            </div>
            <div class="toggle-row">
                <div class="ashore-toggle toggle-control">
                    <label class="switch">
                    <input type="checkbox" v-model="notifyMeNewCommentsEnabled" />
                    <div class="slider round"></div>
                    </label>
                </div>
                <div class="toggle-text">
                    <div class="toggle-title">Notify me about new comments contacts make on proofs I own</div>
                    <div class="toggle-description">Stay on top of reviewer feedback without watching the timeline.</div>
                    <div v-if="notifyMeNewCommentsEnabled" class="nested-field">
                        <label class="field-label" for="notifyMeNewCommentsFrequency">Notification Frequency</label>
                        <div class="select-wrap">
                            <select v-model="userSettings.notifyMeNewComments" class="select-input"
                                id="notifyMeNewCommentsFrequency">
                                <option v-for="(value, label) in NOTIFICATION_FREQUENCY_OPTIONS" :key="label" :value="value">
                                    {{ formatFrequencyLabel(label) }}
                                </option>
                            </select>
                            <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="1 1 5 5 9 1" />
                            </svg>
                        </div>
                    </div>
                    <div v-if="notifyMeNewCommentsEnabled && userSettings.notifyMeNewComments === NOTIFICATION_FREQUENCY_OPTIONS.DAILY" class="nested-field">
                        <label class="field-label" for="notifyMeDailyTime">Daily Send Time</label>
                        <div class="select-wrap">
                            <select v-model="dailyDigestTimeOfDay" class="select-input" id="notifyMeDailyTime">
                                <option v-for="option in digestTimeOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                            <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="1 1 5 5 9 1" />
                            </svg>
                        </div>
                        <div class="toggle-description">Daily digests send at this time ({{ userSettings.reminderTimeZone || 'America/Chicago' }}). One send time applies to all your daily digests.</div>
                    </div>
                </div>
            </div>
        </div>
        <div class="card-foot">
            <a class="primary-btn" @click="updateEmailNotificationSettings" data-cy="updateSignatureBtn">Update</a>
        </div>
    </div>
</template>

<style scoped>
.settings-card {
    background: #fff;
    border: 1px solid rgba(15, 23, 42, 0.06);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
}
.card-head {
    padding: 14px 20px;
    background: #fbfbfc;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    border-radius: 12px 12px 0 0;
}
.card-head-title {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
}
.card-head-subtitle {
    margin-top: 1px;
    font-size: 12.5px;
    font-weight: 400;
    color: #6b7280;
}
.card-foot {
    display: flex;
    justify-content: flex-end;
    padding: 12px 20px;
    background: #fbfbfc;
    border-top: 1px solid rgba(15, 23, 42, 0.06);
    border-radius: 0 0 12px 12px;
}
.toggle-rows {
    display: flex;
    flex-direction: column;
}
.toggle-row {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 15px 20px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.toggle-row:last-child {
    border-bottom: 0;
}
.toggle-control {
    flex-shrink: 0;
    margin-top: 1px;
}
.toggle-control .switch {
    margin-right: 0;
}
.toggle-text {
    flex: 1;
    min-width: 0;
}
.toggle-title {
    font-size: 13.5px;
    font-weight: 600;
    color: #111827;
}
.toggle-description {
    margin-top: 1px;
    font-size: 12.5px;
    font-weight: 400;
    color: #6b7280;
    line-height: 1.5;
}
.nested-field {
    margin-top: 12px;
    max-width: 340px;
}
.field-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: #6b7280;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 6px;
}
.select-wrap {
    position: relative;
}
.select-input {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 100%;
    height: 38px;
    padding: 0 32px 0 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    color: #1f2937;
    background: #fff;
    outline: none;
    cursor: pointer;
    transition: border-color 120ms ease, box-shadow 120ms ease;
}
.select-input:focus {
    border-color: #019256;
    box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.select-caret {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    pointer-events: none;
}
.primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    padding: 0 18px;
    background: #019256;
    color: #fff;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: background-color 120ms ease;
}
.primary-btn:hover {
    background: #017a48;
    color: #fff;
    text-decoration: none;
}
@media (max-width: 575px) {
    .primary-btn {
        width: 100%;
    }
}
</style>
